// File: src/app/AppContent.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// Routing configuration for the admin portal

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AdminProtectedRoute from '../components/admin/AdminProtectedRoute';
import UserListView from '../components/admin/UserListView';
import UserDetailView from '../components/admin/UserDetailView';
import AdminLayout from '../components/admin/AdminLayout';
import EdenAIConfigView from '../components/admin/EdenAIConfigView';
import IntegrationsView from '../components/admin/IntegrationsView';
// legacy SubscriptionOptionsView/SubscriptionDetailView deleted.
// PlansView is the canonical plan editor (Free + Pro, 5 caps each).
import PlansView from '../components/admin/PlansView';
import PromoCodesListView from '../components/admin/PromoCodesListView';
// subscription overview dashboard + drill-downs.
import SubscriptionOverviewView from '../components/admin/SubscriptionOverviewView';
import SubscriptionUsersView from '../components/admin/SubscriptionUsersView';
import SubscriptionCreditsView from '../components/admin/SubscriptionCreditsView';
import EmailConfigView from '../components/admin/EmailConfigView';
import DocumentationConfigView from '../components/admin/DocumentationConfigView';
import AuditLogView from '../components/admin/AuditLogView';
import AuditLogDetailView from '../components/admin/AuditLogDetailView';
import { SchedulerManagementView } from '../components/admin/scheduler';
import { AnalysisTaskListView, AnalysisTaskDetailView, TemplateGallery, TaskDescribeGenerate } from '../components/admin/analysis-tasks';
import AutopilotListView from '../components/admin/autopilot/AutopilotListView';
import AutopilotDetailView from '../components/admin/autopilot/AutopilotDetailView';
import OTPPhoneView from '../components/admin/OTPPhoneView';
import { SyncListView, SyncDetailView } from '../components/admin/sync';
// unified geo-target registry (Google + Microsoft) management.
import GeoLocationsView from '../components/admin/GeoLocationsView';
// OAuth return page for admin-initiated platform connects.
import OAuthCallbackView from '../components/admin/OAuthCallbackView';

const AppContent = () => (
    <AdminProtectedRoute>
        <Routes>
            <Route element={<AdminLayout />}>
                <Route path="/" element={<Navigate to="/users" replace />} />

                <Route path="/users" element={<UserListView />} />
                <Route path="/users/:uid" element={<UserDetailView />} />
                <Route path="/users/:uid/:tab" element={<UserDetailView />} />

                <Route path="/edenai-config" element={<EdenAIConfigView />} />
                <Route path="/email-config" element={<EmailConfigView />} />
                <Route path="/documentation" element={<DocumentationConfigView />} />
                <Route path="/audit" element={<AuditLogView />} />
                <Route path="/audit/:id" element={<AuditLogDetailView />} />
                <Route path="/integrations" element={<IntegrationsView />} />
                <Route path="/integrations/:tab" element={<IntegrationsView />} />
                { /* geo-target registry (Google + Microsoft). */}
                <Route path="/geo" element={<GeoLocationsView />} />
                { /* OAuth return for admin platform connects. */}
                <Route path="/integrations/oauth-callback/:platformId" element={<OAuthCallbackView />} />
                { /* canonical 5-field Plans editor (Free + Pro). */}
                <Route path="/plans" element={<PlansView />} />
                {/* Promotion codes admin CRUD. */}
                <Route path="/promos" element={<PromoCodesListView />} />
                {/* subscription overview + drill-downs. */}
                <Route path="/subscription" element={<SubscriptionOverviewView />} />
                <Route path="/subscription/users" element={<SubscriptionUsersView />} />
                <Route path="/subscription/credits" element={<SubscriptionCreditsView />} />
                {/* Legacy /subscription-options now redirects to /subscription (was /plans). */}
                <Route path="/subscription-options" element={<Navigate to="/subscription" replace />} />
                <Route path="/subscription-options/*" element={<Navigate to="/subscription" replace />} />

                { /* Scheduler Management (Phase 3) */}
                <Route path="/scheduler" element={<SchedulerManagementView />} />

                {/* Sync Observability (Issue #233) */}
                <Route path="/syncs" element={<SyncListView />} />
                <Route path="/syncs/:uid/:syncId" element={<SyncDetailView />} />

                {/* AI Analysis Tasks */}
                <Route path="/analysis-tasks" element={<AnalysisTaskListView />} />
                <Route path="/analysis-tasks/new/templates" element={<TemplateGallery />} />
                <Route path="/analysis-tasks/new/from-template" element={<AnalysisTaskDetailView />} />
                <Route path="/analysis-tasks/new/generate" element={<TaskDescribeGenerate />} />
                <Route path="/analysis-tasks/:id/:tab?" element={<AnalysisTaskDetailView />} />

                { /* OTP Phone Management */}
                <Route path="/otp-phones" element={<OTPPhoneView />} />

                { /* Autopilot */}
                <Route path="/autopilot" element={<AutopilotListView />} />
                <Route path="/autopilot/:uid" element={<AutopilotDetailView />} />
                <Route path="/autopilot/:uid/:tab" element={<AutopilotDetailView />} />

                {/* Catch all - redirect to users */}
                <Route path="*" element={<Navigate to="/users" replace />} />
            </Route>
        </Routes>
    </AdminProtectedRoute>
);

export default AppContent;
